import { Lightning, Stop } from '@phosphor-icons/react';
import {
  getListWorkspaceFlowRunsQueryKey,
  useCancelFlowRun,
  useListWorkspaceFlowRuns,
} from '@reelplicate/api-client/flow-runs';
import type { RunStepDto, WorkspaceFlowRunDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Skeleton,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { formatWhen } from '@/lib/format-when';

import { isOpen } from '../editor/run/run-state';
import { RUN_STATUS, runKind } from '../flow-status';

/** How often the page asks again while anything is still going. */
const POLL_MS = 3000;

/** Where a step is at, e.g. "0 of 1", "3 waiting for review". */
function progress(step: RunStepDto): string {
  switch (step.status) {
    case 'RUNNING':
      return step.tasksTotal > 0 ? `${step.tasksDone} of ${step.tasksTotal}` : 'working';
    case 'WAITING':
      return step.itemsHeld > 0 ? `${step.itemsHeld} waiting for review` : 'waiting';
    case 'SLEEPING':
      return 'measuring';
    case 'FAILED':
      return 'failed';
    default:
      return step.status.toLowerCase();
  }
}

/**
 * `/flows/running` - runs across the workspace: what's going now and where
 * it's at, and what finished in the last day. Runs carry on in the
 * background, so this is where to watch them.
 */
export function RunningPage() {
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const queryClient = useQueryClient();
  const { data: runs } = useListWorkspaceFlowRuns(workspaceId, {
    query: {
      enabled: Boolean(workspaceId),
      refetchInterval: (query) => (query.state.data?.some(isOpen) ? POLL_MS : false),
    },
  });
  const cancel = useCancelFlowRun();
  const [stopping, setStopping] = useState<WorkspaceFlowRunDto | null>(null);

  const open = runs?.filter(isOpen) ?? [];
  const finished = runs?.filter((run) => !isOpen(run)) ?? [];

  const stop = async (run: WorkspaceFlowRunDto) => {
    try {
      await cancel.mutateAsync({ id: workspaceId, runId: run.id });
    } catch {
      return; // Shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey: getListWorkspaceFlowRunsQueryKey(workspaceId),
    });
    toast.success(`Stopped “${run.flowName}”`);
  };

  const columns = (withNotes: boolean): DataTableColumn<WorkspaceFlowRunDto>[] => [
    {
      id: 'flow',
      header: 'Flow',
      width: 260,
      cell: (run) => (
        <RouterLink
          to={`/flows/${run.flowId}?run=${run.id}`}
          className="text-text-primary focus-visible:shadow-focus-ring rounded-xs font-medium outline-none hover:underline"
        >
          {run.flowName}
        </RouterLink>
      ),
    },
    {
      id: 'kind',
      header: 'Run',
      width: 220,
      cell: (run) => (
        <span className="text-text-secondary">
          {runKind(run)} · {formatWhen(run.startedAt)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: 180,
      cell: (run) => (
        <Badge tone={RUN_STATUS[run.status].tone} dot>
          {RUN_STATUS[run.status].label}
        </Badge>
      ),
    },
    withNotes
      ? {
          id: 'notes',
          header: 'Notes',
          cell: (run) => (
            // Cells don't wrap by default; a run's note is worth reading in full.
            <span className="text-text-secondary block min-w-[320px] py-3 break-words whitespace-normal">
              {run.error ?? '—'}
            </span>
          ),
        }
      : {
          id: 'steps',
          header: 'Where it’s at',
          cell: (run) =>
            run.steps.length === 0 ? (
              <span className="text-text-secondary">Starting…</span>
            ) : (
              <ul className="flex flex-col gap-0.5 py-2">
                {run.steps.map((step) => (
                  <li key={step.nodeId} className="text-text-secondary whitespace-normal">
                    <span className="text-text-primary">{step.label}</span> ·{' '}
                    {progress(step)}
                  </li>
                ))}
              </ul>
            ),
        },
    ...(withNotes
      ? []
      : [
          {
            id: 'actions',
            header: <span className="sr-only">Actions</span>,
            width: 120,
            align: 'right' as const,
            cell: (run: WorkspaceFlowRunDto) => (
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={Stop}
                onClick={() => setStopping(run)}
              >
                Stop
              </Button>
            ),
          },
        ]),
  ];

  return (
    <div className="flex flex-col gap-8 p-8">
      <header className="flex min-w-0 flex-col gap-1">
        <h1 className="text-heading-h1 text-text-primary">Running</h1>
        <p className="text-body-md text-text-secondary">
          Flows running now and where they’re at. They carry on when you leave.
        </p>
      </header>

      {!runs ? (
        <Skeleton className="h-40" shape="rectangle" />
      ) : (
        <>
          <section aria-labelledby="running-now" className="flex flex-col gap-3">
            <h2 id="running-now" className="text-heading-h4 text-text-primary">
              Running now
            </h2>
            <DataTable
              label="Running now"
              columns={columns(false)}
              rows={open}
              getRowId={(run) => run.id}
              getRowLabel={(run) => `${run.flowName}, ${runKind(run)}`}
              empty={
                <EmptyState
                  icon={Lightning}
                  title="Nothing running"
                  description="Runs you start, and scheduled ones, show up here while they work."
                  actions={
                    <Button asChild variant="outline">
                      <RouterLink to="/flows">All flows</RouterLink>
                    </Button>
                  }
                />
              }
            />
          </section>

          {finished.length > 0 && (
            <section aria-labelledby="finished-today" className="flex flex-col gap-3">
              <h2 id="finished-today" className="text-heading-h4 text-text-primary">
                Finished today
              </h2>
              <DataTable
                label="Finished today"
                columns={columns(true)}
                rows={finished}
                getRowId={(run) => run.id}
                getRowLabel={(run) => `${run.flowName}, ${runKind(run)}`}
              />
            </section>
          )}
        </>
      )}

      <ConfirmDialog
        open={stopping !== null}
        onOpenChange={(next) => !next && setStopping(null)}
        tone="danger"
        title={`Stop “${stopping?.flowName ?? ''}”?`}
        description="Queued work and reviews are cancelled. What it already made stays."
        confirmLabel="Stop run"
        onConfirm={() => {
          if (stopping) void stop(stopping);
          setStopping(null);
        }}
      />
    </div>
  );
}
